import "@progress/kendo-ui/js/kendo.notification";
import "bootstrap";
import * as _ from "lodash";
import * as moment from "moment";
import { IResponse } from "../interfaces/data";
import { ICustomerSettings } from "../interfaces/user";
import * as Constants from "./constants";
import * as Global from "./global";
import * as Util from "./util";

// display error messages in a module window
export function showErrorMessage(messages: string[]): void {
    if (!messages) { return; }

    let message: string = "";
    let i: number;
    let max: number;

    max = messages.length;
    for (i = 0; i < max; i++) {
        message += messages[i];
    }
    $("#master_errorMessageDialog_Message").text(message);
    $("#master_errorMessageDialog").modal("show");
}

// singleton instance of the confirmation dialog control
let confirmDialogIsCreated: boolean = false;
let confirmDialogOnYes: any = null;
let confirmDialogOnNo: any = null;

// showConfirmDialog
//  display a confirm dialog box with Yes and No buttons
//  message - text to display in the dialog
//  onYes - function to call if user clicks on Yes button
//  onNo - function to call if user clicks on No button
export function showConfirmDialog(message: string, onYes: () => void, onNo?: () => void): void {

    if (!confirmDialogIsCreated) {
        // first time being called, bind the events
        $("#master_confirmDialog_Yes").click(() => {
            $("#master_confirmDialog").modal("hide");
            if (confirmDialogOnYes != null) {
                confirmDialogOnYes();
            }
        });
        $("#master_confirmDialog_No").click(() => {
            $("#master_confirmDialog").modal("hide");
            if (confirmDialogOnNo != null) {
                confirmDialogOnNo();
            }
        });
        confirmDialogIsCreated = true;
    }

    confirmDialogOnYes = onYes;
    confirmDialogOnNo = onNo;
    $("#master_confirmDialog_Message").text(message);
    $("#master_confirmDialog").modal("show");
}

let sessionTimeoutWarningDialogIsCreated: boolean = false;
let sessionTimeoutWarningDialogTimer: number | null = null;
let sessionTimeoutWarningDialogExpirationDateTime: moment.Moment | null = null;
export function showSessionTimeWarningDialog(): void {
    const app = Global.app();

    const clearTimer = () => {
        if (sessionTimeoutWarningDialogTimer) {
            clearInterval(sessionTimeoutWarningDialogTimer);
        }
    };

    if (!sessionTimeoutWarningDialogIsCreated) {
        // first time being called, bind the events
        $("#master_sessionExpirationWarningDialog_ContinueWorking").click(() => {
            clearTimer();
            Util.callWebService("/api/App/SessionKeepAlive", "GET").done((response: IResponse<null>) => {
                if (response.Success) {
                    $("#master_sessionExpirationWarningDialog").modal("hide");
                } else {
                    showErrorMessage(response.ErrorMessages);
                }
            });
        });
        $("#master_sessionExpirationWarningDialog_Logout").click(() => {
            clearTimer();
            app.pageManager().logout();
        });
        sessionTimeoutWarningDialogIsCreated = true;
    }

    // Compute the time when the session will expire
    // Also subtract a few seconds of fudge time to make sure we logout user before server timeout occurs
    sessionTimeoutWarningDialogExpirationDateTime = moment().add(app.global.SessionTimeoutWarningMinutes, "minutes").subtract(20, "seconds");

    // Function to show how long before session expires
    const showTimeToExpiration = () => {
        if (sessionTimeoutWarningDialogExpirationDateTime) {
            $("#master_sessionExpirationWarningDialog_TimeToLogout").text(sessionTimeoutWarningDialogExpirationDateTime.fromNow());
        }
    };

    // Setup timer to update the time remaining display
    sessionTimeoutWarningDialogTimer = window.setInterval(() => {
        if (sessionTimeoutWarningDialogExpirationDateTime && sessionTimeoutWarningDialogExpirationDateTime.isBefore()) {
            // Session has expired
            clearTimer();
            app.pageManager().logout();
        } else {
            showTimeToExpiration();
        }
    }, 5000);

    // Show the dialog
    showTimeToExpiration();
    $("#master_sessionExpirationWarningDialog").modal("show");
}

// displays a wait spinner with a gray background over the entire
// targetContainer tag.
// returns a reference to the wait tag that is needed when the
// indicator is to be removed.
export function showWaitIndicator(targetContainer: JQuery): JQuery {
    const position: JQuery.Coordinates | undefined = targetContainer.offset();
    const left: number = position ? position.left : 0;
    const top: number = position ? position.top : 0;
    const targetWidth: number = _.defaultTo<number>(targetContainer.width(), 0);
    const targetHeight: number = _.defaultTo<number>(targetContainer.height(), 0);

    targetContainer.css({ opacity: "0.5" });

    return $("<div class='three-quarters'></div>").css({
        position: "absolute",
        left: left + (targetWidth / 2),
        top: top + (targetHeight / 2),
    }).appendTo("body");
}

// removes the wait spinner created with the showWaitIndicator function
export function hideWaitIndicator(targetContainer: JQuery, waitContainer: JQuery): void {
    targetContainer.css({ opacity: "1" });
    $(waitContainer).remove();
}

// used to toggle a required error message
// if the value is empty, sets the errorMessageName field to true
export function setRequiredStringError(value: string, errorMessageName: string, viewModel: any): boolean {
    let isValid: boolean = true;

    if (value == null || value === "") {
        isValid = false;
    }
    // call the knockout function to update the fields
    viewModel[errorMessageName](!isValid);

    return isValid;
}

export function GetCustomerSetting(customerSettings: ICustomerSettings[], settingKey: string, defaultValue: string): string {
    const setting = customerSettings.filter((x) => x.SettingKey === settingKey);
    let settingValue = defaultValue;
    if (setting && setting.length > 0) {
        settingValue = setting[0].Value;
    }

    return settingValue;
}

export function getKendoDateFormat(dateSetting: string): string {
    return Constants.KENDO_DATE_FORMAT_MAP[dateSetting];
}
export function getKendoTimeFormat(timeSetting: string): string {
    return Constants.KENDO_TIME_FORMAT_MAP[timeSetting];
}
export function getMomentDateFormat(dateSetting: string): string {
    return Constants.MOMENT_DATE_FORMAT_MAP[dateSetting];
}
export function getMomentTimeFormat(timeSetting: string): string {
    return Constants.MOMENT_TIME_FORMAT_MAP[timeSetting];
}

export function getKendoNumericFormat(mask: string, numberOfDecimals: number) {
    let format = mask;
    if (!format) {
        format = "#"; // default format
    }
    if (numberOfDecimals > 0) {
        format = format + ".";
        for (let i = 0; i < numberOfDecimals; i++) {
            format = format + "0";
        }
    }

    return format;
}

export function formatValueFromControlType(value: string, controlTypeID: number): string {
    let returnValue = value;

    if (value &&
        (controlTypeID === Constants.QuestionType.DATE ||
         controlTypeID === Constants.QuestionType.DATETIME ||
         controlTypeID === Constants.QuestionType.TIME)) {
        const app = Global.app();
        const customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        const dateFormat = GetCustomerSetting(customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT);
        const timeFormat = GetCustomerSetting(customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);
        if (controlTypeID === Constants.QuestionType.DATE) {
            returnValue = kendo.toString(new Date(`${value}T00:00:00`), getKendoDateFormat(dateFormat));
        } else if (controlTypeID === Constants.QuestionType.DATETIME) {
            returnValue = kendo.toString(new Date(value), `${getKendoDateFormat(dateFormat)} ${getKendoTimeFormat(timeFormat)}`).toString();
        } else if (controlTypeID === Constants.QuestionType.TIME) {
            returnValue = kendo.toString(new Date(`2000-01-01T${value}`), getKendoTimeFormat(timeFormat)).toString();
        }
    }

    return  returnValue;
}

export function setRequiredString(value: KnockoutObservable<string>, errorFlag: KnockoutObservable<boolean>): boolean {
    const isValid: boolean = !Util.isNullOrEmpty(value());

    errorFlag(!isValid);

    return isValid;
}

export function setRequiredNumber(value: KnockoutObservable<number>, errorFlag: KnockoutObservable<boolean>,
    mustBeGreaterThenZero: boolean = false): boolean {
    let isValid: boolean = true;

    if (value() == null) {
        isValid = false;
    } else if (mustBeGreaterThenZero && value() <= 0) {
        isValid = false;
    }

    // call the knockout function to update the fields
    errorFlag(!isValid);

    return isValid;
}

export function validateMinMaxNumericValues(minEntry: KnockoutObservable<string>, maxEntry: KnockoutObservable<string>,
    allowNegative: boolean, errorFlag: KnockoutObservable<boolean>) {

    let isValid = true;

    const min: number = parseInt(minEntry(), 10);
    const max: number = parseInt(maxEntry(), 10);

    if (!isNaN(min) && !allowNegative && min < 0) {
        isValid = false;
    } else if (!isNaN(max) && !allowNegative && max < 0) {
        isValid = false;
    } else if (!isNaN(min) && !isNaN(max) && max < min) {
        isValid = false;
    }

    errorFlag(!isValid);

    return isValid;
}

export enum messageTypeValues { success, error, info }

// showNotificationMessage
//  displays a temporary pop-up message
//  messages - a string to display in the message
//  messageType - can be succes, error or info
let notificationMessage: kendo.ui.Notification;
export function showNotificationMessage(message: string, messageType: messageTypeValues): void {
    if (!notificationMessage) {
        notificationMessage = $("#master_notificationMessage").kendoNotification({
            width: 400,
            position: {
                bottom: 20,
                right: 80,
            },
        }).data("kendoNotification");
    }

    // every master page should have a tag called NotificationMessage
    let className: string = "";

    switch (messageType) {
        case messageTypeValues.success:
            className = "success";
            break;
        case messageTypeValues.error:
            className = "error";
            break;
        case messageTypeValues.info:
            className = "info";
            break;
    }

    notificationMessage.show(message, className);
}

export function resizeGrid(gridContainer: JQuery, height: number): void {

    const dataArea: JQuery<HTMLElement> = gridContainer.find(".k-grid-content");
    const newHeight: number = height - 8;
    const gridInnerHeight: number = _.defaultTo<number>(gridContainer.innerHeight(), 0);
    const dataInnerHeight: number = _.defaultTo<number>(dataArea.innerHeight(), 0);
    const diff: number = gridInnerHeight - dataInnerHeight;

    gridContainer.height(newHeight);
    dataArea.height(newHeight - diff);
}

export function isDateTimeValid(date: any, time: any, dateTime: any, dateFormat: string, timeFormat: string): boolean {
    const momentDateFormat = getMomentDateFormat(dateFormat);
    const momentTimeFormat = getMomentTimeFormat(timeFormat);

    const dateTimeString = dateTime ? dateTime : `${date ? date : moment().format(momentDateFormat)} ${time ? time : moment().format(momentTimeFormat)}`;
    const dateTimeMoment = moment(dateTimeString, `${momentDateFormat} ${momentTimeFormat}`, true);
    return dateTimeMoment.isValid();
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/ui.ts